// ─────────────────────────────────────────────────────────────────────────────
// V2PurchaseOrders — /dashboard/purchase-orders in the v2 design.
// Endpoints are v1's: GET/POST /api/purchase-orders, PUT /:id, PATCH /:id/status,
// POST /:id/receive, POST /:id/pdf. Body shapes identical to v1. "Convert to bill"
// navigates to purchase entry with state.importPoId, exactly as v1.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, inr } from "./primitives";
import { Combo, Field, LineItems, PageHead, Stat, fromPurchaseItems, newLine, purchasePrice, purchaseTotals, toPurchaseItems, useProducts } from "./kit";

const BASE = `${API_BASE}/api`;
const json = () => ({ "Content-Type": "application/json", "auth-token": localStorage.getItem("token") });
const today = () => new Date().toISOString().split("T")[0];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS = { DRAFT: ["Draft", "neutral"], SENT: ["Sent", "info"], PARTIAL: ["Part received", "warn"], RECEIVED: ["Received", "good"], CANCELLED: ["Cancelled", "bad"] };
const open = (s) => ["DRAFT", "SENT", "PARTIAL"].includes(s);

export function POStatus({ status }) {
  const [label, tone] = STATUS[status] || STATUS.DRAFT;
  return <span className={`v2-status v2-status--${tone}`}>{label}</span>;
}

export default function V2PurchaseOrders({ showAlert }) {
  usePageTitle("Purchase Orders");
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("purchases", "write");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("OPEN");
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const newBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await fetch(`${BASE}/purchase-orders`, { headers: json() }); const d = await r.json(); setList(Array.isArray(d) ? d : []); }
    catch { setList([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = list.filter((p) => (status === "ALL" || (status === "OPEN" ? open(p.status) : p.status === status)) &&
    (!q.trim() || `${p.poNumber} ${p.supplierId?.supplierName || ""}`.toLowerCase().includes(q.trim().toLowerCase())));
  const openList = list.filter((p) => open(p.status));
  const awaiting = list.filter((p) => p.status === "SENT" || p.status === "PARTIAL");
  const late = awaiting.filter((p) => p.expectedDate && new Date(p.expectedDate) < new Date());

  const patchStatus = async (po, s) => {
    try {
      const r = await fetch(`${BASE}/purchase-orders/${po._id}/status`, { method: "PATCH", headers: json(), body: JSON.stringify({ status: s }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't update");
      showAlert?.(s === "SENT" ? `${po.poNumber} marked sent` : `${po.poNumber} cancelled`, "success");
      setConfirm(null); if (viewing?._id === po._id) setViewing(d._id ? d : null); load();
    } catch (e) { setConfirm((c) => (c ? { ...c, error: e.message } : c)); showAlert?.(e.message, "danger"); }
  };
  const pdf = async (po) => {
    try {
      const r = await fetch(`${BASE}/purchase-orders/${po._id}/pdf`, { method: "POST", headers: json() });
      if (!r.ok) throw new Error();
      const a = document.createElement("a"); a.href = URL.createObjectURL(await r.blob()); a.download = `PO-${po.poNumber}.pdf`; a.click();
    } catch { showAlert?.("Couldn't generate the PDF", "danger"); }
  };
  const menu = (po) => [
    { label: "View order", glyph: "invoice", onSelect: () => setViewing(po) },
    { label: "Download PDF", glyph: "download", onSelect: () => pdf(po) },
    ...(canWrite && open(po.status) ? [
      "divider",
      ...(po.status === "DRAFT" ? [{ label: "Mark as sent", glyph: "arrowUpRight", onSelect: () => patchStatus(po, "SENT") }] : []),
      { label: "Receive goods", glyph: "truck", onSelect: () => setReceiving(po) },
      ...(!po.convertedToPurchase ? [{ label: "Convert to bill", glyph: "invoice", onSelect: () => navigate("/dashboard/purchase-entry", { state: { importPoId: po._id } }) }] : []),
      ...(po.status === "DRAFT" ? [{ label: "Edit", glyph: "note", onSelect: () => setEditing(po) }] : []),
      "divider",
      { label: "Cancel order", glyph: "close", danger: true, onSelect: () => setConfirm(po) },
    ] : []),
  ];

  return (
    <div className="v2p">
      <PageHead eyebrow="Payables" title="Purchase orders" sub="Order from suppliers, then receive goods into stock."
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setEditing("new")}><Glyph name="plus" size={17} stroke={2.2} /> New order</button>} />
      <div className="v2k-stats">
        <Stat label="Open orders" value={loading ? "—" : openList.length} note={inr(openList.reduce((a, p) => a + (p.grandTotal || 0), 0)) + " committed"} />
        <Stat label="Awaiting delivery" value={loading ? "—" : awaiting.length} note={late.length ? `${late.length} past expected date` : "Nothing late"} tone={late.length ? "warn" : undefined} />
        <Stat label="Drafts" value={loading ? "—" : list.filter((p) => p.status === "DRAFT").length} note="Not yet sent" />
        <Stat label="Received" value={loading ? "—" : list.filter((p) => p.status === "RECEIVED").length} note="All time" />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={status} onChange={setStatus} options={[{ value: "OPEN", label: "Open" }, { value: "DRAFT", label: "Drafts" }, { value: "RECEIVED", label: "Received" }, { value: "CANCELLED", label: "Cancelled" }, { value: "ALL", label: "All" }]} />
        <div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="PO number or supplier" aria-label="Search orders" /></div>
      </div>
      <section className="v2-card v2i-card">
        {loading ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2].map((i) => <Skel key={i} h={58} r={16} />)}</div>
          : shown.length === 0 ? <div className="v2c-empty"><Duo name="truck" size={48} /><strong>{list.length ? "Nothing in this view" : "No purchase orders yet"}</strong><span>Raise a PO, send it to the supplier, and receive goods straight into stock.</span></div>
          : <div className="v2i-rows">{shown.map((po) => {
            const isLate = (po.status === "SENT" || po.status === "PARTIAL") && po.expectedDate && new Date(po.expectedDate) < new Date();
            return (
              <div key={po._id} className={`v2cn-row${po.status === "CANCELLED" ? " is-void" : ""}`}>
                <button type="button" className="v2cn-open" onClick={() => setViewing(po)}>
                  <Duo name="truck" size={30} />
                  <span className="v2-row-main">
                    <span className="v2i-no">{po.poNumber}{po.convertedToPurchase && <span className="v2cn-link">Billed</span>}</span>
                    <span className="v2-row-sub">{po.supplierId?.supplierName || "Supplier"} · {fmtDate(po.poDate)}{po.expectedDate ? ` · expected ${fmtDate(po.expectedDate)}` : ""}</span>
                  </span>
                  <span className="v2cn-end">
                    <span className="v2i-total">{inr(po.grandTotal, { decimals: 2 })}</span>
                    {isLate ? <span className="v2-status v2-status--bad"><Glyph name="alert" size={13} stroke={2.2} /> Late</span> : <POStatus status={po.status} />}
                  </span>
                </button>
                <MenuButton items={menu(po)} className="v2-icon-btn v2i-more" label={`Actions for ${po.poNumber}`} width={230} />
              </div>);
          })}</div>}
      </section>

      <PODetail po={viewing} onClose={() => setViewing(null)} canWrite={canWrite} onPdf={pdf}
        onSent={(po) => patchStatus(po, "SENT")} onReceive={(po) => { setViewing(null); setReceiving(po); }}
        onConvert={(po) => navigate("/dashboard/purchase-entry", { state: { importPoId: po._id } })} onEdit={(po) => { setViewing(null); setEditing(po); }} />
      <POEditor value={editing} originRef={newBtn} onClose={() => setEditing(null)}
        onSaved={(d, msg) => { setEditing(null); showAlert?.(msg, "success"); load(); setViewing(d); }} />
      <ReceiveSheet po={receiving} onClose={() => setReceiving(null)}
        onDone={(d) => { setReceiving(null); showAlert?.(`Goods received · bill ${d.purchase?.purchaseNumber || ""} created, stock updated`, "success"); load(); }} />
      <Sheet open={!!confirm} onClose={() => setConfirm(null)} width={440} title={`Cancel ${confirm?.poNumber || ""}?`}
        footer={<><button type="button" className="v2-btn" onClick={() => setConfirm(null)}>Keep</button><button type="button" className="v2-btn v2c-btn-danger" onClick={() => patchStatus(confirm, "CANCELLED")}>Cancel order</button></>}>
        <p className="v2c-remove-text">The order is marked cancelled. Goods already received and bills already created are not affected.</p>
        {confirm?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {confirm.error}</div>}
      </Sheet>
    </div>
  );
}

function PODetail({ po, onClose, canWrite, onPdf, onSent, onReceive, onConvert, onEdit }) {
  const keep = useRef(po); if (po) keep.current = po;
  const p = keep.current;
  if (!p) return null;
  return (
    <Sheet open={!!po} onClose={onClose} width={640} title={p.poNumber} subtitle={`${p.supplierId?.supplierName || "Supplier"} · ${fmtDate(p.poDate)}`}
      footer={<>
        <button type="button" className="v2-btn" onClick={() => onPdf(p)}><Glyph name="download" size={15} /> PDF</button>
        {canWrite && p.status === "DRAFT" && <button type="button" className="v2-btn" onClick={() => onEdit(p)}>Edit</button>}
        {canWrite && p.status === "DRAFT" && <button type="button" className="v2-btn" onClick={() => onSent(p)}>Mark sent</button>}
        {canWrite && open(p.status) && !p.convertedToPurchase && <button type="button" className="v2-btn" onClick={() => onConvert(p)}>Convert to bill</button>}
        {canWrite && open(p.status) && <button type="button" className="v2-btn v2-btn--primary" onClick={() => onReceive(p)}><Glyph name="truck" size={15} /> Receive goods</button>}
      </>}>
      <div className="v2pub-receipt">
        <div className="v2pub-receipt-top">
          <div><span className="v2-label">Order value</span><div className="v2pub-receipt-amt"><Money value={p.grandTotal} /></div></div>
          <POStatus status={p.status} />
        </div>
        <dl className="v2pub-meta">
          <div><dt>Ordered</dt><dd>{fmtDate(p.poDate)}</dd></div>
          <div><dt>Expected</dt><dd>{fmtDate(p.expectedDate)}</dd></div>
          <div><dt>Supply</dt><dd>{p.supplyType === "inter" ? "Inter-state" : "Intra-state"}</dd></div>
          <div><dt>Items</dt><dd>{(p.items || []).length}</dd></div>
        </dl>
        <div className="v2-inset">{(p.items || []).map((it, i) => (
          <div key={i} className="v2-inset-row v2-inset-row--static">
            <span className="v2-row-main"><span className="v2-row-title v2-wrap">{it.name}</span>
              <span className="v2-row-sub">{it.qty} {it.unit || ""} × {inr(it.price, { decimals: 2 })} · GST {it.gstRate}%{it.qtyReceived ? ` · received ${it.qtyReceived}` : ""}</span></span>
            <b>{inr((it.qty || 0) * (it.price || 0), { decimals: 2 })}</b>
          </div>))}</div>
        {(p.terms || p.notes) && <p className="v2-row-sub">{[p.terms, p.notes].filter(Boolean).join(" · ")}</p>}
      </div>
    </Sheet>
  );
}

function POEditor({ value, originRef, onClose, onSaved }) {
  const isNew = value === "new";
  const { products, loading } = useProducts();
  const [suppliers, setSuppliers] = useState([]);
  const [f, setF] = useState({ supplierId: "", poDate: today(), expectedDate: "", supplyType: "intra", terms: "", notes: "" });
  const [lines, setLines] = useState(() => [newLine()]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const was = useRef(null);
  useEffect(() => { fetch(`${BASE}/suppliers`, { headers: json() }).then((r) => r.json()).then((d) => setSuppliers(Array.isArray(d) ? d : [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!value || was.current === value) return;
    was.current = value; setErr("");
    if (isNew) { setF({ supplierId: "", poDate: today(), expectedDate: "", supplyType: "intra", terms: "", notes: "" }); setLines([newLine()]); return; }
    setF({ supplierId: value.supplierId?._id || value.supplierId || "", poDate: value.poDate?.split("T")[0] || today(), expectedDate: value.expectedDate?.split("T")[0] || "", supplyType: value.supplyType || "intra", terms: value.terms || "", notes: value.notes || "" });
    setLines(value.items?.length ? fromPurchaseItems(value.items) : [newLine()]);
  }, [value]); // eslint-disable-line
  useEffect(() => { if (!value) was.current = null; }, [value]);
  const t = useMemo(() => purchaseTotals(lines, f.supplyType), [lines, f.supplyType]);
  const save = async (status) => {
    setErr("");
    if (!f.supplierId) { setErr("Choose a supplier."); return; }
    const items = toPurchaseItems(lines);
    if (!items.some((i) => i.name && i.qty > 0)) { setErr("Add at least one item."); return; }
    setBusy(status);
    try {
      const r = await fetch(isNew ? `${BASE}/purchase-orders` : `${BASE}/purchase-orders/${value._id}`, { method: isNew ? "POST" : "PUT", headers: json(), body: JSON.stringify({ ...f, items, status }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save");
      onSaved(d, status === "SENT" ? "Order saved and marked sent" : "Draft saved");
    } catch (x) { setErr(x.message); } finally { setBusy(""); }
  };
  const supplierItems = suppliers.map((s) => ({ id: s._id, label: s.supplierName, sub: s.supplierGSTIN || s.contactPerson || "" }));
  return (
    <Sheet open={!!value} onClose={onClose} originRef={isNew ? originRef : undefined} width={780} title={isNew ? "New purchase order" : `Edit ${value?.poNumber || ""}`}
      footer={<>
        <span className="v2cn-foot-total">Order <b>{inr(t.total, { decimals: 2 })}</b></span>
        <button type="button" className="v2-btn" disabled={!!busy} onClick={() => save("DRAFT")}>{busy === "DRAFT" ? "Saving…" : "Save draft"}</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={!!busy} onClick={() => save("SENT")}>{busy === "SENT" ? "Saving…" : "Save & mark sent"}</button>
      </>}>
      <div className="v2-form">
        <Field label="Supplier"><Combo items={supplierItems} value={f.supplierId} onChange={(id) => setF((p) => ({ ...p, supplierId: id }))} placeholder="Choose a supplier" searchPlaceholder="Search suppliers" ariaLabel="Supplier" /></Field>
        <div className="v2-form-row">
          <Field label="Order date"><input className="v2-input" type="date" value={f.poDate} onChange={(e) => setF((p) => ({ ...p, poDate: e.target.value }))} /></Field>
          <Field label="Expected delivery" optional><input className="v2-input" type="date" min={f.poDate} value={f.expectedDate} onChange={(e) => setF((p) => ({ ...p, expectedDate: e.target.value }))} /></Field>
        </div>
        <Field label="Supply"><Segmented ariaLabel="Supply type" value={f.supplyType} onChange={(v) => setF((p) => ({ ...p, supplyType: v }))} options={[{ value: "intra", label: "Intra-state · CGST+SGST" }, { value: "inter", label: "Inter-state · IGST" }]} /></Field>
        <div className="v2c-section-title" style={{ margin: "6px 0 0 4px" }}>Items · priced at purchase price</div>
        <LineItems items={lines} onChange={setLines} products={products} productsLoading={loading} defaultGst={18} priceFor={purchasePrice} compact />
        <TotalsBox t={t} supplyType={f.supplyType} label="Order total" />
        <div className="v2-form-row">
          <Field label="Terms" optional><input className="v2-input" value={f.terms} onChange={(e) => setF((p) => ({ ...p, terms: e.target.value }))} placeholder="e.g. 30 days credit, FOR destination" /></Field>
          <Field label="Notes" optional><input className="v2-input" value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} /></Field>
        </div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </div>
    </Sheet>
  );
}

export function TotalsBox({ t, supplyType, label = "Total" }) {
  return (
    <div className="v2-inset v2cn-totals">
      <div className="v2-inset-row v2-inset-row--static"><span>Subtotal</span><b>{inr(t.subtotal, { decimals: 2 })}</b></div>
      {supplyType === "inter"
        ? <div className="v2-inset-row v2-inset-row--static"><span>IGST</span><b>{inr(t.igst, { decimals: 2 })}</b></div>
        : <><div className="v2-inset-row v2-inset-row--static"><span>CGST</span><b>{inr(t.cgst, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>SGST</span><b>{inr(t.sgst, { decimals: 2 })}</b></div></>}
      <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>{label}</span><b>{inr(t.total, { decimals: 2 })}</b></div>
    </div>
  );
}

function ReceiveSheet({ po, onClose, onDone }) {
  const keep = useRef(po); if (po) keep.current = po;
  const p = keep.current;
  const [inv, setInv] = useState(""); const [date, setDate] = useState(today()); const [supply, setSupply] = useState("intra");
  const [qty, setQty] = useState([]); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  useEffect(() => { if (po) { setInv(""); setDate(today()); setSupply(po.supplyType || "intra"); setQty(po.items.map((i) => i.qty)); setErr(""); } }, [po]);
  if (!p) return null;
  const go = async () => {
    if (!inv.trim()) { setErr("Enter the supplier's invoice number."); return; }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BASE}/purchase-orders/${p._id}/receive`, { method: "POST", headers: json(), body: JSON.stringify({ supplierInvoiceNo: inv, purchaseDate: date, supplyType: supply, qtyReceived: qty }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't receive");
      onDone(d);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!po} onClose={onClose} width={600} title={`Receive ${p.poNumber}`} subtitle="Creates a purchase bill and adds stock"
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="button" className="v2-btn v2-btn--primary" disabled={busy} onClick={go}>{busy ? "Receiving…" : "Receive goods"}</button></>}>
      <div className="v2-form">
        <div className="v2-form-row">
          <Field label="Supplier's invoice no."><input className="v2-input v2-mono" autoFocus value={inv} onChange={(e) => setInv(e.target.value)} /></Field>
          <Field label="Received on"><input className="v2-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        </div>
        <Field label="Supply"><Segmented ariaLabel="Supply" value={supply} onChange={setSupply} options={[{ value: "intra", label: "Intra-state" }, { value: "inter", label: "Inter-state" }]} /></Field>
        <div className="v2c-section-title" style={{ margin: "6px 0 0 4px" }}>Quantities received</div>
        <div className="v2-inset">{p.items.map((it, i) => (
          <div key={i} className="v2-inset-row v2-inset-row--static">
            <span className="v2-row-main"><span className="v2-row-title v2-wrap">{it.name}</span><span className="v2-row-sub">ordered {it.qty} {it.unit || ""}</span></span>
            <span className="v2cn-qty">
              <button type="button" className="v2-icon-btn" aria-label="Less" disabled={Number(qty[i]) <= 0} onClick={() => setQty((q) => q.map((v, j) => (j === i ? Math.max(0, Number(v) - 1) : v)))}>−</button>
              <input className="v2-input" type="number" min="0" value={qty[i] ?? 0} onChange={(e) => setQty((q) => q.map((v, j) => (j === i ? e.target.value : v)))} aria-label={`Received ${it.name}`} />
              <button type="button" className="v2-icon-btn" aria-label="More" onClick={() => setQty((q) => q.map((v, j) => (j === i ? Number(v) + 1 : v)))}>+</button>
            </span>
          </div>))}</div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </div>
    </Sheet>
  );
}
